import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUppyEvent, useUppyState } from '@uppy/react';
import { Button } from '@/components/ui/button';
import { AlertCircle, CameraIcon, CheckCircle2 } from 'lucide-react';
import '@uppy/file-input/dist/style.css';
import { useUppy } from '@/providers/uppy-provider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Progress } from './ui/progress';

// Initialize Supabase client (replace with your actual Supabase URL and anon key)

interface ImageUploadProps {
  folderName: string;
  onUploadComplete: (imageUrls: string[]) => void;
}

export default function ImageUploader({ onUploadComplete }: ImageUploadProps) {
  const { t } = useTranslation();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const { uppy } = useUppy();
  const [url, setUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useUppyEvent(uppy, 'upload', () => {
    if (isOpen) {
      setUploading(true);
    }
  });

  useUppyEvent(uppy, 'complete', (r) => {
    if (isOpen) {
      const successfulUploads = r.successful;
      if (successfulUploads?.length) {
        const imageUrls = successfulUploads.map((file) => {
          const { bucketName, objectName } = file.meta;
          return `${bucketName}/${objectName}`;
        });
        setUploading(false);
        setUploadSuccess(true);
        onUploadComplete(imageUrls);
      }
    }
  });

  useUppyEvent(uppy, 'error', () => {
    if (isOpen) {
      setUploadError(t('uploadFailed'));
    }
  });
  const progress = useUppyState(uppy, (s) => s.totalProgress);
  const startUpload = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };
  const [isOpen, setIsOpen] = useState(false);

  function getPreviewUrl(file: File) {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const [file] = e.target.files;
      uppy.addFile({
        name: file.name,
        type: file.type,
        data: file,
      });
      const urlString = await getPreviewUrl(file);
      setUrl(urlString);
      setIsOpen(true);
    }
  };
  return (
    <>
      <input
        ref={inputRef}
        accept="image/*"
        onChange={handleUpload}
        name="file"
        title="file"
        type="file"
        className="hidden"
      />
      <Button onClick={startUpload} className="inline-flex gap-2" variant="outline">
        <CameraIcon className="h-6 w-6" />
        <span>{t('addphoto')}</span>
      </Button>
      <Dialog open={isOpen} onOpenChange={(val) => setIsOpen(val)}>
        <DialogContent className="flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('uploadImages')}</DialogTitle>
            <DialogDescription>{t('uploadImagesDescription')}</DialogDescription>
          </DialogHeader>

          {url && (
            <img
              alt="Upload Preview"
              className="w-full h-48 object-cover rounded-lg"
              src={url}
            />
          )}
          {uploading && <Progress value={progress} className="h-6 w-full" />}
          {uploadError && (
            <div className="flex items-center text-destructive mt-4">
              <AlertCircle className="mr-2 h-4 w-4" />
              <span>{uploadError}</span>
            </div>
          )}
          {uploadSuccess && (
            <div className="flex items-center text-green-500 mt-4">
              <CheckCircle2 className="mr-2 h-4 w-4" />
              <span>{t('uploadSuccess')}</span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
